import { LegalPageLayout } from './LegalPageLayout';

const LAST_UPDATED = 'May 26, 2026';

const sections = [
  { id: 'operator', label: 'Operator' },
  { id: 'accounts', label: 'Accounts and teams' },
  { id: 'meta-connection', label: 'Meta connection' },
  { id: 'customer-content', label: 'Customer content' },
  { id: 'ai-recommendations', label: 'AI recommendations' },
  { id: 'permitted-use', label: 'Permitted use' },
  { id: 'changes-beta', label: 'Product changes' },
  { id: 'suspension', label: 'Suspension' },
  { id: 'warranty-liability', label: 'Warranty and liability' },
  { id: 'governing-law', label: 'Governing law' },
  { id: 'meta-affiliation', label: 'Meta affiliation' },
];

export function TermsOfService() {
  return (
    <LegalPageLayout
      title="Terms of Service"
      description="These terms describe AdVerdict account, Meta connection, content, and AI recommendation responsibilities."
      lastUpdated={LAST_UPDATED}
      sections={sections}
    >
      <section id="operator" className="space-y-3">
        <h2 className="text-xl font-semibold text-primary">Operator</h2>
        <p>
          AdVerdict is operated by Hendrik Wels, Falkenried 74, 20251
          Hamburg, Germany. Questions about these terms, privacy, or deletion
          requests can be sent to{' '}
          <a
            className="break-words text-brand hover:underline"
            href="mailto:hendrik.wels1@gmail.com"
          >
            hendrik.wels1@gmail.com
          </a>
          .
        </p>
      </section>

      <section id="accounts" className="space-y-3">
        <h2 className="text-xl font-semibold text-primary">
          Accounts and team responsibilities
        </h2>
        <p>
          Users are responsible for maintaining accurate account information,
          protecting login credentials, inviting appropriate team members, and
          ensuring team administrators have authority to manage projects,
          creatives, Meta connections, imports, and settings inside AdVerdict.
        </p>
      </section>

      <section id="meta-connection" className="space-y-3">
        <h2 className="text-xl font-semibold text-primary">
          Meta connection requirements
        </h2>
        <p>
          A user who connects Meta represents that they have authority to connect
          the relevant Meta ad accounts, Pages, campaigns, ads, creatives,
          performance metrics, and related assets. Users must comply with Meta
          terms, advertising policies, account-role requirements, and any
          permission limits shown during OAuth or App Review.
        </p>
      </section>

      <section id="customer-content" className="space-y-3">
        <h2 className="text-xl font-semibold text-primary">
          Customer content and creative ownership
        </h2>
        <p>
          Customers keep ownership of their uploaded or imported customer
          content, including creative files, brand context, project metadata,
          and authorized Meta advertising data. Customers are responsible for
          having the rights needed to upload, import, analyze, and use that
          content in AdVerdict. For customer-provided or customer-authorized
          content, AdVerdict may act as a processor or service provider when
          processing on the customer&apos;s instructions, while remaining
          controller for service operation, security, legal compliance,
          aggregated or product analytics where permitted, support, and
          administration records.
        </p>
      </section>

      <section id="ai-recommendations" className="space-y-3">
        <h2 className="text-xl font-semibold text-primary">
          AI analysis and Scale/Fix/Kill recommendations
        </h2>
        <p>
          AdVerdict provides decision-support analysis, including Scale/Fix/Kill
          recommendations, evidence summaries, and creative improvement
          suggestions. These outputs may be incomplete or incorrect and should
          be reviewed by qualified humans before advertising, budget, legal, or
          platform-policy decisions are made.
        </p>
      </section>

      <section id="permitted-use" className="space-y-3">
        <h2 className="text-xl font-semibold text-primary">
          Permitted use and prohibited conduct
        </h2>
        <p>
          Users may use AdVerdict for lawful creative analysis and authorized ad
          account workflows. Users must not upload unlawful content, access data
          without permission, evade security controls, misuse Meta permissions,
          reverse engineer the service, overload the platform, or use outputs to
          violate law, platform policy, or third-party rights.
        </p>
      </section>

      <section id="changes-beta" className="space-y-3">
        <h2 className="text-xl font-semibold text-primary">
          Beta features and product changes
        </h2>
        <p>
          AdVerdict may change features, models, report formats, integrations,
          metrics, reports, or recommendations as the product develops. Beta or
          experimental features may be less stable and may change or be removed.
        </p>
      </section>

      <section id="suspension" className="space-y-3">
        <h2 className="text-xl font-semibold text-primary">
          Suspension and termination
        </h2>
        <p>
          Access may be suspended or terminated if a user violates these terms,
          creates security risk, lacks authority over connected accounts or
          content, violates Meta or advertising platform requirements, or uses
          the service in a way that may harm AdVerdict, customers, or others.
        </p>
      </section>

      <section id="warranty-liability" className="space-y-3">
        <h2 className="text-xl font-semibold text-primary">
          Warranty and liability
        </h2>
        <p>
          AdVerdict is intended to support creative decisions, not guarantee ad
          performance, legal compliance, account approval, Meta App Review
          approval, or business results. Users remain responsible for reviewing
          outputs and deciding whether advertising, budget, legal, or
          platform-policy actions are appropriate.
        </p>
      </section>

      <section id="governing-law" className="space-y-3">
        <h2 className="text-xl font-semibold text-primary">Governing law</h2>
        <p>
          These terms are governed by the laws of Germany. The venue and forum
          for disputes is Hamburg, Germany, unless mandatory consumer or data
          protection law provides otherwise.
        </p>
      </section>

      <section id="meta-affiliation" className="space-y-3">
        <h2 className="text-xl font-semibold text-primary">
          No Meta endorsement
        </h2>
        <p>
          AdVerdict is not affiliated with, endorsed by, sponsored by, or
          otherwise approved by Meta, Facebook, Instagram, or their affiliates.
          Meta product names are used only to describe customer-authorized
          integration workflows.
        </p>
      </section>
    </LegalPageLayout>
  );
}

export default TermsOfService;
